import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ClockIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { business } from '../data/business';
import { SectionHeading } from './SectionHeading';
type Inquiry = 'Order or refund request' | 'Manuscript submission' | 'Rights & permissions' | 'General inquiry';
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    type: 'General inquiry' as Inquiry,
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const inputClass = 'w-full bg-transparent border-b border-ink/20 py-3 text-ink placeholder-ink/40 focus:border-gold focus:outline-none transition-colors';
  return <section id="contact" className="w-full bg-parchment py-28" data-id="element-89" data-mp="gifgv7sokv9g">
      <div className="mx-auto max-w-5xl px-6" data-id="element-90" data-mp="bougj93m7p73">
        <SectionHeading eyebrow="Customer Service" title="Contact us" description="Reach a real person about an order, a refund, a rights request, or a manuscript. Our support team is here during business hours." data-id="element-91" data-mp="3jvkzohsloskv" />

        <div className="mt-16 grid md:grid-cols-[1fr_1.3fr] gap-14" data-id="element-92" data-mp="3b2hnectoylrt">
          <address className="not-italic space-y-8" data-id="element-93" data-mp="3f4csztinnlbe">
            <div className="flex items-start gap-4" data-id="element-94" data-mp="3f2rtrqlebe77">
              <MailIcon className="text-gold mt-1 shrink-0" size={20} data-id="element-95" data-mp="314trhjkcn882" />
              <div data-id="element-96" data-mp="363wpwl5tqg72">
                <p className="font-serif text-lg text-ink" data-id="element-97" data-mp="364g6nyjqmyji">Email support</p>
                <a href={`mailto:${business.email}`} className="text-ink/65 hover:text-gold transition-colors" data-id="element-98" data-mp="2s4omfg2molx2">
                  {business.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-99" data-mp="qdk08zjgsdk4">
              <PhoneIcon className="text-gold mt-1 shrink-0" size={20} data-id="element-100" data-mp="uf5i6zauvi16" />
              <div data-id="element-101" data-mp="ucawz5k0pt6p">
                <p className="font-serif text-lg text-ink" data-id="element-102" data-mp="23bc02u2qpzur">Phone</p>
                <a href={business.phoneHref} className="text-ink/65 hover:text-gold transition-colors" data-id="element-103" data-mp="239hjfryi4twh">
                  {business.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-104" data-mp="1pcktrtu4dih6">
              <ClockIcon className="text-gold mt-1 shrink-0" size={20} data-id="element-105" data-mp="1pe5lzcsyopbv" />
              <div data-id="element-106" data-mp="2m1k3ldacqsbh">
                <p className="font-serif text-lg text-ink" data-id="element-107" data-mp="2m46b5cx8t7tp">Support hours</p>
                <p className="text-ink/65" data-id="element-108" data-mp="2i103q1ye9aln">{business.hours}</p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-109" data-mp="2d871bkqeeecq">
              <MapPinIcon className="text-gold mt-1 shrink-0" size={20} data-id="element-110" data-mp="28b6tp5dwh3ji" />
              <div data-id="element-111" data-mp="288uciufyux7i">
                <p className="font-serif text-lg text-ink" data-id="element-112" data-mp="35psuzl42mu9x">Business address</p>
                <p className="text-ink/65" data-id="element-113" data-mp="35rvv4xyxu3sd">
                  {business.address.line1}
                  <br data-id="element-114" data-mp="30x9fujpqjr30" />
                  {business.address.line2}
                  <br data-id="element-115" data-mp="38bsexyp6uhb5" />
                  {business.address.line3}
                  <br data-id="element-116" data-mp="3d92195ha6cur" />
                  {business.address.line4}
                </p>
              </div>
            </div>
            <p className="text-ink/55 text-sm leading-relaxed border-t border-ink/10 pt-6" data-id="element-117" data-mp="3i7lod97m1uop">
              {business.responseTime} For refunds and returns, please review our{' '}
              <a href="#refunds" className="text-gold hover:underline" data-id="element-118" data-mp="3r9v185qcq059">
                Refund &amp; Return Policy
              </a>{' '}
              and include your order number.
            </p>
          </address>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center text-center bg-paper p-10 shadow-sm" data-id="element-119" data-mp="4uk0loto5zl6">
              <CheckCircleIcon className="text-gold mb-4" size={44} data-id="element-120" data-mp="it9vqywxhp6w" />
              <h3 className="font-serif text-2xl text-ink" data-id="element-121" data-mp="irgrfq9sfh22">Thank you</h3>
              <p className="text-ink/65 mt-2 max-w-sm" data-id="element-122" data-mp="559k9j82glse">
                Your message has been received. A member of our editorial team will
                be in touch soon.
              </p>
            </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-123" data-mp="eyglcrvrxfmo">
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-124" data-mp="9y2ps9bczy5j">
                <div data-id="element-125" data-mp="3ict3piz09iqu">
                  <label htmlFor="name" className="sr-only" data-id="element-126" data-mp="3iedx359l2vn4">Name</label>
                  <input id="name" required value={form.name} onChange={e => setForm({
                ...form,
                name: e.target.value
              })} placeholder="Your name" className={inputClass} data-id="element-127" data-mp="3ec0x6xhipry9" />
                </div>
                <div data-id="element-128" data-mp="17k01nnv8h58i">
                  <label htmlFor="email" className="sr-only" data-id="element-129" data-mp="17jgk489hwiv0">Email</label>
                  <input id="email" type="email" required value={form.email} onChange={e => setForm({
                ...form,
                email: e.target.value
              })} placeholder="Email address" className={inputClass} data-id="element-130" data-mp="12l5c832rgiff" />
                </div>
              </div>
              <div data-id="element-131" data-mp="2gj9krd5dkxbz">
                <label htmlFor="type" className="sr-only" data-id="element-132" data-mp="2girr8oa3kx7r">Inquiry type</label>
                <select id="type" value={form.type} onChange={e => setForm({
              ...form,
              type: e.target.value as Inquiry
            })} className={`${inputClass} cursor-pointer`} data-id="element-133" data-mp="348vj5ydxb8ix">
                  <option data-id="element-134" data-mp="2zgav7dhp7pnb">General inquiry</option>
                  <option data-id="element-135" data-mp="2zeranap0esfq">Order or refund request</option>
                  <option data-id="element-136" data-mp="3n5n7wnc8ifud">Manuscript submission</option>
                  <option data-id="element-137" data-mp="3rxeruf0vxnbj">Rights &amp; permissions</option>
                </select>
              </div>
              <div data-id="element-138" data-mp="5nzlq3oejkvu">
                <label htmlFor="message" className="sr-only" data-id="element-139" data-mp="5o9axp1mho0h">Message</label>
                <textarea id="message" required rows={4} value={form.message} onChange={e => setForm({
              ...form,
              message: e.target.value
            })} placeholder="Your message" className={`${inputClass} resize-none`} data-id="element-140" data-mp="owlqxna44uq" />
              </div>
              <button type="submit" className="px-8 py-3.5 bg-ink text-paper tracking-wide hover:bg-gold hover:text-ink transition-colors" data-id="element-141" data-mp="3iwm48ymthqla">
                Send message
              </button>
            </form>}
        </div>
      </div>
    </section>;
}